import logoLight from "@/assets/logo-light.png";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const navLinks = [
    { href: "/", label: "Inicio", hash: null },
    { href: "/servicios", label: "Servicios", hash: null },
    { href: "/", label: "Cómo trabajamos", hash: "proceso" },
    { href: "/", label: "Clientes", hash: "testimonios" },
    { href: "/sobre-nosotros", label: "Nosotros", hash: null },
    { href: "/contacto", label: "Contacto", hash: null },
  ];

  // Track active section on scroll (only on homepage)
  useEffect(() => {
    if (!isHome) {
      setActiveSection(null);
      return;
    }

    const sections = ["proceso", "testimonios", "servicios"];
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      // Check if at top of page
      if (scrollPosition < 300) {
        setActiveSection(null);
        return;
      }

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const scrollToHash = useCallback((hash: string) => {
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent, link: typeof navLinks[0]) => {
    // Handle hash links (scroll to section)
    if (link.hash) {
      e.preventDefault();
      if (isHome) {
        scrollToHash(link.hash);
      } else {
        navigate("/");
        scrollToHash(link.hash);
      }
    }
    // Handle "Inicio" click
    else if (link.href === "/" && link.label === "Inicio") {
      e.preventDefault();
      if (isHome) {
        // Already on home - scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // On another page - navigate to home
        navigate("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  }, [isHome, navigate, scrollToHash]);

  const isLinkActive = (link: typeof navLinks[0]) => {
    // For hash links, check if section is active
    if (link.hash) {
      return isHome && activeSection === link.hash;
    }
    // For Inicio, active when on home and no section is active
    if (link.href === "/" && link.label === "Inicio") {
      return isHome && !activeSection;
    }
    // For regular pages
    return location.pathname === link.href && link.href !== "/";
  };

  const linkBaseClass = "text-sm font-medium transition-all duration-200";
  const linkInactiveClass = "text-primary-foreground/70 hover:text-primary-foreground";
  const linkActiveClass = "text-primary-foreground relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-tera-dark/95 backdrop-blur-md border-b border-primary-foreground/10">
      <div className="container flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2">
          <img src={logoLight} alt="Tera Seguridad" className="h-10 md:h-12 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = isLinkActive(link);
            const needsClickHandler = link.hash || (link.href === "/" && link.label === "Inicio");
            
            return needsClickHandler ? (
              <button
                key={link.label}
                onClick={(e) => handleNavClick(e, link)}
                className={cn(linkBaseClass, isActive ? linkActiveClass : linkInactiveClass)}
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={cn(linkBaseClass, isActive ? linkActiveClass : linkInactiveClass)}
              >
                {link.label}
              </Link>
            );
          })}
          <Button variant="hero" size="sm" asChild>
            <Link to="/contacto">Evaluación sin costo</Link>
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-primary-foreground p-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <nav className="md:hidden bg-tera-dark border-t border-primary-foreground/10 py-4 animate-fade-in">
          <div className="container flex flex-col gap-2">
            {navLinks.map((link) => {
              const isActive = isLinkActive(link);
              const needsClickHandler = link.hash || (link.href === "/" && link.label === "Inicio");
              
              return needsClickHandler ? (
                <button
                  key={link.label}
                  onClick={(e) => handleNavClick(e, link)}
                  className={cn(
                    "text-left py-2 px-3 rounded-lg transition-all duration-200 w-full",
                    isActive 
                      ? "bg-primary/20 text-primary-foreground border-l-2 border-primary" 
                      : "text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/5"
                  )}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={cn(
                    "py-2 px-3 rounded-lg transition-all duration-200",
                    isActive 
                      ? "bg-primary/20 text-primary-foreground border-l-2 border-primary" 
                      : "text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/5"
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
            <Button variant="hero" className="mt-4" asChild>
              <Link to="/contacto">Evaluación sin costo</Link>
            </Button>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
